import { useState } from 'react';
import { agreementsPlatformApi } from '@/modules/agreements/services/platform-api';
import { describeUnknownAgreementFailure } from '@/modules/agreements/shared';
import { useNotification } from '@/shared/ui/notification';

/**
 * The content field: its HTML value and the interaction flags its error surface needs.
 *
 * The invalid surface belongs to an operator who HAS acted — an empty field that was
 * never touched is not an error, it is a form not yet filled in. A submit attempt counts
 * as acting even though the publish button is disabled while the field is empty.
 */
function useContentField() {
  const [value, setValue] = useState('');
  const [touched, setTouched] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  return {
    value,
    isEmpty: value.trim().length === 0,
    invalid: value.trim().length === 0 && (touched || submitAttempted),
    markEdited: (html: string) => {
      setValue(html);
      setTouched(true);
    },
    markSubmitAttempted: () => setSubmitAttempted(true),
    reset: () => {
      setValue('');
      setTouched(false);
      setSubmitAttempted(false);
    },
  };
}

/**
 * The publish-version form's state and submit.
 *
 * Extracted so the form component stays a rendering concern. The server assigns the version
 * number; the client never sends one.
 *
 * The OUTCOME of the submit is reported as a notification, not as inline text. The operator
 * pressed a button and the result belongs to that action, so it appears where the action was
 * taken and does not push the form around. A failure keeps the form's values intact so the
 * operator can correct and retry without retyping.
 *
 * `content` is the HTML string the rich text editor emitted; the server stores it as the
 * version's content.
 */
export function useVersionPublish(documentId: string, onPublished: (versionId: string) => void) {
  const { notify } = useNotification();
  const [locale, setLocale] = useState('en');
  const [isDefaultLocale, setIsDefaultLocale] = useState(true);
  const [title, setTitle] = useState('');
  const content = useContentField();
  const [effectiveAt, setEffectiveAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);

  const localeValid = /^[a-z]{2}(-[A-Z]{2})?$/.test(locale);
  const canSubmit = localeValid && title.trim().length > 0 && !content.isEmpty && !busy;

  async function submit(): Promise<void> {
    content.markSubmitAttempted();
    if (!canSubmit) return;
    setBusy(true);
    try {
      const version = await agreementsPlatformApi.publishVersion(documentId, {
        locale,
        isDefaultLocale,
        title: title.trim(),
        content: content.value,
        effectiveAt: new Date(`${effectiveAt}T00:00:00.000Z`).toISOString(),
        attachments: [],
      });
      setTitle('');
      content.reset();
      notify({ tone: 'success', title: 'Version published' });
      onPublished(version.versionId);
    } catch (caught) {
      notify({
        tone: 'error',
        title: 'Could not publish version',
        description: describeUnknownAgreementFailure(caught),
      });
    } finally {
      setBusy(false);
    }
  }

  return {
    locale,
    setLocale,
    isDefaultLocale,
    setIsDefaultLocale,
    title,
    setTitle,
    content: content.value,
    setContent: content.markEdited,
    effectiveAt,
    setEffectiveAt,
    busy,
    localeValid,
    canSubmit,
    contentInvalid: content.invalid,
    submit,
  };
}
